import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArchiveRestore, Archive, MoreHorizontal, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useDebounced } from "@/hooks/useFetch";
import { useStaff } from "@/context/StaffAuth";
import { ConfirmDialog, DemoBadge, EmptyState, ErrorState, Field, PageHeader, Pager, StatusBadge, TableSkeleton, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

const initialForm = (fields, item) => {
  const f = {};
  fields.forEach((fd) => {
    const v = item ? item[fd.name] : undefined;
    if (fd.type === "switch") f[fd.name] = v ?? fd.default ?? false;
    else if (fd.type === "number") f[fd.name] = v ?? fd.default ?? 0;
    else f[fd.name] = v ?? fd.default ?? "";
  });
  return f;
};

const serialize = (fields, form) => {
  const out = {};
  fields.forEach((fd) => {
    let v = form[fd.name];
    if (fd.type === "number") v = Number(v);
    if (fd.type === "select" && fd.nullable && (v === "" || v === "__none__")) v = null;
    if (typeof v === "string" && fd.optional && v.trim() === "") v = fd.nullable ? null : "";
    out[fd.name] = v;
  });
  return out;
};

const FieldInput = ({ fd, form, setForm, items, idPrefix }) => {
  const id = `${idPrefix}-${fd.name}`;
  const set = (v) => setForm((s) => ({ ...s, [fd.name]: v }));
  const common = { id, "data-testid": `${idPrefix}-${fd.name}-input` };
  if (fd.type === "textarea") return <Field label={fd.label} htmlFor={id} hint={fd.hint} required={fd.required} className={fd.span === 2 ? "sm:col-span-2" : ""}><Textarea rows={fd.rows || 3} value={form[fd.name]} onChange={(e) => set(e.target.value)} maxLength={fd.max} {...common} /></Field>;
  if (fd.type === "switch") return (
    <Field label={fd.label} htmlFor={id} hint={fd.hint} className={fd.span === 2 ? "sm:col-span-2" : ""}>
      <div className="flex items-center gap-3 h-10"><Switch checked={!!form[fd.name]} onCheckedChange={set} {...common} /><span className="text-sm text-muted-foreground">{form[fd.name] ? "Yes" : "No"}</span></div>
    </Field>
  );
  if (fd.type === "select") {
    const opts = typeof fd.options === "function" ? fd.options(form, items) : fd.options || [];
    const val = form[fd.name] === null || form[fd.name] === undefined || form[fd.name] === "" ? (fd.nullable ? "__none__" : "") : String(form[fd.name]);
    return (
      <Field label={fd.label} htmlFor={id} hint={fd.hint} required={fd.required} className={fd.span === 2 ? "sm:col-span-2" : ""}>
        <Select value={val} onValueChange={(v) => set(v === "__none__" ? null : v)}>
          <SelectTrigger id={id} data-testid={`${idPrefix}-${fd.name}-select`}><SelectValue placeholder={fd.placeholder || "Select…"} /></SelectTrigger>
          <SelectContent>
            {fd.nullable && <SelectItem value="__none__">{fd.nonePlaceholder || "None"}</SelectItem>}
            {opts.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    );
  }
  return <Field label={fd.label} htmlFor={id} hint={fd.hint} required={fd.required} className={fd.span === 2 ? "sm:col-span-2" : ""}><Input type={fd.type === "number" ? "number" : fd.type || "text"} value={form[fd.name]} onChange={(e) => set(e.target.value)} placeholder={fd.placeholder} maxLength={fd.max} min={fd.min} max={fd.maxVal} required={fd.required} {...common} /></Field>;
};

/**
 * Generic admin CRUD page for reference entities backed by /api/admin/{resource}.
 */
export const ReferenceCrud = ({ resource, label, labelPlural, eyebrow = "Catalog", description, fields, columns, writePerm, searchPlaceholder, pageSize = 50, extraListParams, canDelete = true }) => {
  const { can } = useStaff();
  const writable = can(writePerm);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const dq = useDebounced(q, 300);
  const status = params.get("status") || "active";
  const page = Number(params.get("page") || 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | item
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type, item}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/${resource}`, { params: { q: dq, status: status === "all" ? "" : status, page, page_size: pageSize, ...(extraListParams || {}) } });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [resource, dq, status, page, pageSize, extraListParams]);

  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (v === "" || v === null || v === undefined) n.delete(k); else n.set(k, String(v));
    if (k !== "page") n.delete("page");
    setParams(n, { replace: true });
  };

  const openNew = () => { setForm(initialForm(fields)); setEditing({}); };
  const openEdit = (item) => { setForm(initialForm(fields, item)); setEditing(item); };

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const body = serialize(fields, form);
      if (editing?.id) {
        await api.put(`/admin/${resource}/${editing.id}`, body);
        toast.success(`${label} updated`);
      } else {
        await api.post(`/admin/${resource}`, body);
        toast.success(`${label} created`);
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, `Could not save ${label.toLowerCase()}`));
    } finally {
      setBusy(false);
    }
  };

  const act = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const { type, item } = confirm;
      if (type === "delete") await api.delete(`/admin/${resource}/${item.id}`);
      else await api.post(`/admin/${resource}/${item.id}/${type}`);
      toast.success(`${label} ${type === "delete" ? "deleted" : type === "archive" ? "archived" : "restored"}`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const items = data?.items || [];
  const idPrefix = `${resource}-form`;
  const title = useMemo(() => labelPlural || `${label}s`, [label, labelPlural]);

  return (
    <div data-testid={`admin-${resource}-page`}>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={writable && <Button onClick={openNew} className="rounded-lg" data-testid={`admin-${resource}-new-button`}><Plus className="h-4 w-4 mr-2" />New {label.toLowerCase()}</Button>}
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setParam("q", e.target.value); }} placeholder={searchPlaceholder || `Search ${title.toLowerCase()}…`} className="pl-9 bg-card rounded-lg" data-testid={`admin-${resource}-search-input`} />
        </div>
        <Tabs value={status} onValueChange={(v) => setParam("status", v)}>
          <TabsList className="rounded-lg">
            <TabsTrigger value="active" data-testid={`admin-${resource}-filter-active`}>Active</TabsTrigger>
            <TabsTrigger value="archived" data-testid={`admin-${resource}-filter-archived`}>Archived</TabsTrigger>
            <TabsTrigger value="all" data-testid={`admin-${resource}-filter-all`}>All</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="bg-card border rounded-xl overflow-hidden shadow-[0_1px_0_hsl(var(--shadow-1))]">
        {loading && !data ? <TableSkeleton cols={columns.length + 2} /> : error ? <ErrorState message={error} onRetry={load} /> : items.length === 0 ? (
          <EmptyState title={`No ${title.toLowerCase()} yet`} description={q ? "Try a different search term." : `Create your first ${label.toLowerCase()} to start merchandising.`} action={writable && !q && <Button onClick={openNew} data-testid={`admin-${resource}-empty-new-button`}><Plus className="h-4 w-4 mr-2" />New {label.toLowerCase()}</Button>} />
        ) : (
          <Table data-testid="admin-data-table">
            <TableHeader className="bg-secondary/60">
              <TableRow>
                {columns.map((c) => <TableHead key={c.key} className={`${thCls} ${c.className || ""}`}>{c.label}</TableHead>)}
                <TableHead className={thCls}>Status</TableHead>
                <TableHead className={`${thCls} w-12`}><span className="sr-only">Actions</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className={rowCls} data-testid={`admin-${resource}-row`}>
                  {columns.map((c, i) => (
                    <TableCell key={c.key} className={`${tdCls} ${c.className || ""}`}>
                      {i === 0 ? (
                        <button type="button" onClick={() => (writable ? openEdit(item) : null)} className="text-left font-medium hover:underline underline-offset-4 inline-flex items-center gap-2" data-testid={`admin-${resource}-row-title`}>
                          {c.render ? c.render(item) : item[c.key]}
                          {item.is_demo && <DemoBadge />}
                        </button>
                      ) : c.render ? c.render(item) : (item[c.key] ?? <span className="text-muted-foreground">—</span>)}
                    </TableCell>
                  ))}
                  <TableCell className={tdCls}><StatusBadge status={item.status} /></TableCell>
                  <TableCell className={tdCls}>
                    {writable && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions" data-testid={`admin-${resource}-row-menu`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => openEdit(item)} data-testid={`admin-${resource}-edit-action`}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          {item.status === "archived" ? (
                            <DropdownMenuItem onSelect={() => setConfirm({ type: "restore", item })} data-testid={`admin-${resource}-restore-action`}><ArchiveRestore className="h-4 w-4 mr-2" />Restore</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onSelect={() => setConfirm({ type: "archive", item })} data-testid={`admin-${resource}-archive-action`}><Archive className="h-4 w-4 mr-2" />Archive</DropdownMenuItem>
                          )}
                          {canDelete && <><DropdownMenuSeparator /><DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setConfirm({ type: "delete", item })} data-testid={`admin-${resource}-delete-action`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem></>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {data && <div className="mt-3"><Pager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} onPage={(p) => setParam("page", p)} /></div>}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`admin-${resource}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium">{editing?.id ? `Edit ${label.toLowerCase()}` : `New ${label.toLowerCase()}`}</DialogTitle>
            <DialogDescription>{editing?.id ? `Changes are audited and applied immediately.` : `All required fields are validated server-side.`}</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {fields.map((fd) => <FieldInput key={fd.name} fd={fd} form={form} setForm={setForm} items={items} idPrefix={idPrefix} />)}
            <DialogFooter className="sm:col-span-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} data-testid={`${idPrefix}-cancel-button`}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid={`${idPrefix}-save-button`}>{busy ? "Saving…" : editing?.id ? "Save changes" : `Create ${label.toLowerCase()}`}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.type === "delete" ? `Delete ${confirm?.item?.name}?` : confirm?.type === "archive" ? `Archive ${confirm?.item?.name}?` : `Restore ${confirm?.item?.name}?`}
        description={confirm?.type === "delete" ? "Deletion is permanent and only allowed when nothing references this record. Otherwise archive it." : confirm?.type === "archive" ? "Archived records are hidden from the storefront and from pickers, but history is preserved." : "The record becomes active again."}
        confirmLabel={confirm?.type === "delete" ? "Delete" : confirm?.type === "archive" ? "Archive" : "Restore"}
        destructive={confirm?.type === "delete"}
        busy={busy}
        onConfirm={act}
      />
    </div>
  );
};
